import { siteConfig } from '@/config/site';
import clientRequest, { showLoginModal } from '@/lib/client-request';
import {
  EventStreamContentType,
  fetchEventSource,
} from '@microsoft/fetch-event-source';
import { getCookie } from '../actions/cookies';

export type AnswerItem = {
  title: string;
  key: string;
  content: string;
};

export interface ChatMessageItem {
  question: string;
  answer: AnswerItem[];
  progress: number;
  id: string;
  status: string;
  references: ReferenceItem[];
  referencesTranslation: ReferenceItem[];
  outline: OutlineItem;
}

export interface ChatMessageResult {
  uid: string;
  sid: string;
  cid: string;
}

export interface ChatMessage {
  result:
    | ChatMessageResult
    | string
    | OutlineItem
    | ReferenceItem[]
    | null;
  code: null;
  message: string;
  version: string;
  log_id: string;
  type:
    | 'backend_conversation_created'
    | 'query_rewrite'
    | 'ref_docs'
    | 'summary_append'
    | 'backend_outline_generated'
    | 'all_done';
}

export interface LLMChatRequest<T> {
  platform?: string;
  path: string;
  body: {
    messages?: LLMMessage[];
    doc_ids?: string[];
    mode?: string;
    chat_gateway?: string;
    style?: string;
    src?: string;
    dest?: string;
    [key: string]: unknown;
  };
  ctrl: AbortController;
  onMessage: (message: T) => void;
  onOpen: () => void;
  onError: (error: number | string | Error) => void;
  onClose: () => void;
}

interface Author {
  last_name: string;
  fore_name: string;
  initials: string;
  affiliations?: Array<{
    name: string;
  }>;
}

export interface ReferenceItem {
  pmcid?: string;
  pmid: string;
  figure_urls?: string[];
  pub_day: string;
  title: string;
  abstract: string;
  publisher: string;
  publisher_abbr: string;
  pub_year: number;
  pub_month: number;
  link: string;
  doi: string;
  authors: Author[];
  doc_id: string;
  pub_source_str: string;
  translation?: {
    [key: string]: {
      title: string;
      abstract: string;
    };
  };
}

export async function llmChat<T = ChatMessage>(request: LLMChatRequest<T>) {
  const token = await getCookie(siteConfig.tokenName);
  return await fetchEventSource(
    process.env.NEXT_PUBLIC_API_BASE_URL + request.path,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Auth-Token': token,
      },
      body: JSON.stringify(request.body),
      openWhenHidden: true,
      signal: request.ctrl.signal,
      async onopen(response) {
        if (
          response.ok &&
          response.headers.get('content-type') === EventStreamContentType
        ) {
          request.onOpen();
          return;
        } else if (
          response.status > 401 &&
          response.status < 500 &&
          response.status !== 429
        ) {
          request.onError(response.status);
        } else if (response.status === 401) {
          showLoginModal(request.platform);
          request.onError(401);
        } else {
          request.onError(response.status);
        }
      },
      onmessage(msg) {
        if (msg.event === 'error') {
          request.onError(msg.data);
        } else {
          try {
            request.onMessage(JSON.parse(msg.data));
          } catch {
            request.onError(new Error('Invalid SSE data'));
          }
        }
      },
      onclose() {
        request.onClose();
      },
      onerror(err) {
        request.onError(err);
        throw err;
      },
    },
  );
}

export interface LLMMessage {
  role: string;
  content: string;
  ref_docs?: ReferenceItem[];
}

export interface GenerateOutlineRequest {
  messages: LLMMessage[];
  mode: string;
  chat_gateway?: string;
}

export interface OutlineItem {
  status: string;
  title: string;
  outline: string;
}

export function generateOutline(
  cid: string,
): Promise<DataResponse<OutlineItem>> {
  return clientRequest.post(`/ai_conversation/${cid}/generate_outline`);
}

export function translateAbstract(
  cid: string,
  index: number,
): Promise<DataResponse<ReferenceItem>> {
  return clientRequest.post(`/ai_conversation/${cid}/translate_doc/${index}`);
}

export function handleLikeOrDislike({
  cid,
  like,
  dislike,
}: {
  cid: string;
  like?: boolean;
  dislike?: boolean;
}): Promise<DataResponse<LikeOrDislike>> {
  return clientRequest.post(`/ai_conversation/${cid}/like_or_dislike`, {
    like,
    dislike,
  });
}

export interface LikeOrDislike {
  like: boolean;
  dislike: boolean;
}

export function getLikeOrDislike(
  cid: string,
): Promise<DataResponse<LikeOrDislike>> {
  return clientRequest.get(`/ai_conversation/${cid}/get_like_or_dislike`);
}
